import { Version } from '../../core/models/version'
import { Engineer } from '../../organisation/models/engineer'
import { alias, AliasedConstructor } from '../../rest/decorators/alias'
import { RestQuery } from '../../rest/utilities/rest-query'
import {
  DynamicBinding,
  SerialisationConfig,
} from '../../rest/utilities/serialisable'
import { BaseJobAllocation } from './base-job-allocation'
import { Job } from './job'
import { JobStatus } from './job-status'
import { SalesPurchaseOrder } from './sales-purchase-order'
import { Update } from './update'

export class JobAllocation extends BaseJobAllocation {
  public static override serialisationConfig: SerialisationConfig =
    new SerialisationConfig(() => new JobAllocation(), {
      job: new SerialisationConfig(() => new Job()),
      engineer: Engineer.serialisationConfig,
      internalStatus: JobStatus.serialisationConfig,
      externalStatus: JobStatus.serialisationConfig,
      order: SalesPurchaseOrder.serialisationConfig,
      updates: Update.serialisationConfig,
      firstVersion: new SerialisationConfig(() => new Version()),
      lastVersion: new SerialisationConfig(() => new Version()),
      currentVersion: new SerialisationConfig(() => new Version()),
    })
  override mandatory = ['id']

  @alias('order')
  order!: SalesPurchaseOrder | null
  @alias('updates')
  updates!: Update[]

  get startedUpdate(): Update | null {
    return this.updates && this.updates.length > 0
      ? (this.updates.find((update) => update.status.value === 'started') ??
          null)
      : null
  }

  get completedUpdate(): Update | null {
    return this.updates && this.updates.length > 0
      ? (this.updates.find((update) => update.status.value === 'completed') ??
          null)
      : null
  }

  constructor() {
    super((JobAllocation as unknown as AliasedConstructor)._alias)
  }

  override serialise(): object {
    const data = super.serialise() as DynamicBinding
    if (Object.prototype.hasOwnProperty.call(data, 'order')) {
      delete data['order']
    }
    return data
  }

  static create<Data>(attrs: Data) {
    return new JobAllocation().update(attrs, JobAllocation.serialisationConfig)
  }
}

export interface Filters {
  job?: number | string
  keywords?: string
  exclude?: string
  range?: {
    start: string
    end: string
  }
  before?: string
  after?: string
  customer?: number | string
  end_user?: number | string
  engineer?: number | string
  status?: string
  registration_chassis?: string
  type?: string
  has_pending_post_work_images?: boolean
  archive?: string
}
type Ordering =
  'date' | '-date' | 'start_at' | '-start_at' | 'end_at' | '-end_at'
type Includes =
  | 'job'
  | 'job.customer'
  | 'job.endUser'
  | 'job.firstAllocation'
  | 'job.lastAllocation'
  | 'job.allocations'
  | 'job.allocations.updates'
  | 'job.stockGroups'
  | 'job.stockGroups.products'
  | 'job.stockGroups.products.product'
  | 'job.stockGroups.products.stockItems'
  | 'job.stockGroups.products.stockItems.location'
  | 'engineer'
  | 'engineer.category'
  | 'updates'
  | 'postWorkCounts'

export class JobAllocationQuery extends RestQuery<
  Filters,
  Ordering,
  Includes
> {}
